import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, X, Trash2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { COUNTRIES, dialFor, withDial } from "../lib/countries";
import { officeFor } from "../lib/regions";
import { useQuoteCart } from "../context/QuoteCartContext";
import { useCatalogLocale } from "../lib/localizeCatalog";
import { CountrySelect } from "./CountrySelect";
import { PrivacyConsent } from "./PrivacyConsent";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const inputCls =
  "rounded-none bg-white border-border focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-gray-950 text-gray-950 placeholder:text-gray-400";
const labelCls = "font-mono-tech text-[10px] uppercase tracking-[0.2em] text-gray-500";

const useItemName = () => {
  const { t } = useTranslation();
  const loc = useCatalogLocale();
  return (it) =>
    it.rawName
      ? t("part2.fullName", {
          part: loc.partName(it.rawName),
          brand: it.brand,
          model: it.model,
          type: loc.machType(it.machType),
        })
      : it.name;
};

const PhotoAttach = ({ photo, setPhoto, testid }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/quotes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(r.data.url);
      toast.success(t("rq.photoAttached"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("rq.uploadFailed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div>
      {!photo ? (
        <button
          type="button"
          data-testid={testid}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 border border-dashed border-gray-400 px-3.5 py-2 text-xs text-gray-600 hover:border-gray-950 hover:text-gray-950 transition-colors disabled:opacity-50"
        >
          <Camera size={14} />
          {uploading ? t("rq.uploadingBtn") : t("rq.attach")}
        </button>
      ) : (
        <div className="flex items-center gap-3">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={t("imgAlt.attachedPart")} className="h-14 w-14 object-cover border border-border" />
          <button type="button" onClick={() => setPhoto("")} className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600">
            <X size={13} /> {t("rq.remove")}
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};

const AddStage = () => {
  const { t } = useTranslation();
  const { pending, addItem, close } = useQuoteCart();
  const itemName = useItemName();
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");
  if (!pending) return null;
  const confirm = () => {
    if (!qty.trim()) return;
    addItem({ ...pending, qty: qty.trim(), note: note.trim(), photo });
    toast.success(t("qc.addedToast"));
  };
  return (
    <div className="p-6 space-y-5" data-testid="qc-add-stage">
      <div className="border border-border bg-gray-50 p-4">
        <p className="font-semibold text-sm text-gray-950">{itemName(pending)}</p>
        <p className="font-mono-tech text-[11px] text-gray-500 mt-1">
          {pending.brand} {pending.model}
          {pending.code && <span className="ml-2 bg-yellow-500 text-gray-950 font-bold px-1.5">{pending.code}</span>}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="qc-qty" className={labelCls}>{t("rq.lQty")} *</Label>
        <Input id="qc-qty" data-testid="qc-add-qty-input" required inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="qc-note" className={labelCls}>{t("rq3.lDesc")}</Label>
        <Textarea id="qc-note" data-testid="qc-add-note-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("rq.phWhichParts")} className={`${inputCls} border border-border p-3`} />
      </div>
      <PhotoAttach photo={photo} setPhoto={setPhoto} testid="qc-add-photo-button" />
      <div className="flex gap-3 pt-2">
        <Button
          data-testid="qc-add-confirm-button"
          disabled={!qty.trim()}
          onClick={confirm}
          className="flex-1 bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black rounded-none font-mono-tech text-xs uppercase tracking-[0.2em] py-5 transition-colors"
        >
          {t("qc.confirmAdd")}
        </Button>
        <Button data-testid="qc-add-cancel-button" variant="outline" onClick={close} className="rounded-none font-mono-tech text-xs uppercase tracking-[0.2em] py-5">
          {t("qc.cancel")}
        </Button>
      </div>
    </div>
  );
};

const CartStage = () => {
  const { t } = useTranslation();
  const { items, updateQty, removeItem, setStage, close } = useQuoteCart();
  const itemName = useItemName();
  return (
    <div className="flex flex-col h-full" data-testid="qc-cart-stage">
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-gray-500 py-10 text-center" data-testid="qc-empty">{t("qc.empty")}</p>
        )}
        {items.map((it) => (
          <div key={it.slug} className="border border-border p-3.5" data-testid={`qc-item-${it.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-gray-950 leading-snug">{itemName(it)}</p>
                <p className="font-mono-tech text-[11px] text-gray-500 mt-0.5">
                  {it.brand} {it.model}
                  {it.code && <span className="ml-2 bg-yellow-500 text-gray-950 font-bold px-1">{it.code}</span>}
                </p>
                {it.note && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{it.note}</p>}
              </div>
              {it.photo && <img src={`${process.env.REACT_APP_BACKEND_URL}${it.photo}`} alt="" className="h-11 w-11 object-cover border border-border shrink-0" />}
            </div>
            <div className="flex items-center gap-3 mt-2.5">
              <span className={labelCls}>{t("rq.lQty")}</span>
              <Input
                data-testid={`qc-item-qty-${it.slug}`}
                inputMode="numeric"
                value={it.qty}
                onChange={(e) => updateQty(it.slug, e.target.value)}
                className={`${inputCls} h-8 w-20`}
              />
              <button
                type="button"
                data-testid={`qc-item-remove-${it.slug}`}
                onClick={() => removeItem(it.slug)}
                className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
              >
                <Trash2 size={13} /> {t("rq.remove")}
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-border p-5 space-y-3 bg-gray-50">
        <button type="button" data-testid="qc-add-more-button" onClick={close} className="w-full text-left font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-600 hover:text-gray-950">
          {t("qc.addMore")}
        </button>
        <Button
          data-testid="qc-to-form-button"
          disabled={items.length === 0}
          onClick={() => setStage("form")}
          className="w-full bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black rounded-none font-mono-tech text-xs uppercase tracking-[0.2em] py-6 transition-colors"
        >
          {t("qc.toForm")}
        </Button>
      </div>
    </div>
  );
};

const FormStage = ({ onDone }) => {
  const { t, i18n } = useTranslation();
  const { items, setStage, clear } = useQuoteCart();
  const [form, setForm] = useState({ name: "", company: "", email: "", country: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dial = dialFor(form.country);
  const office = form.country ? officeFor(form.country) : null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", {
        lang: (i18n.language || "en").slice(0, 2),
        name: form.name,
        email: form.email,
        company: form.company,
        country: form.country,
        phone: withDial(form.phone, form.country),
        message: form.message,
        part_slug: items[0]?.slug || "",
        quantity: items.length === 1 ? items[0].qty : "",
        parts: items.map((it) => ({
          part_slug: it.slug,
          qty: it.qty,
          description: it.note || "",
          photo: it.photo || "",
        })),
        routed_office: office ? office.company : "",
      });
      onDone(items.length);
      clear();
      setStage("done");
      toast.success(t("quote2.toastOk"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("quote2.toastErr"));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col h-full" data-testid="qc-form-stage">
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <button type="button" data-testid="qc-back-button" onClick={() => setStage("cart")} className="font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-gray-950">
          {t("qc.back")}
        </button>
        <p className="text-xs text-gray-600" data-testid="qc-items-count">{t("qc.itemsCount", { count: items.length })}</p>
        <div className="space-y-1.5">
          <Label htmlFor="qcf-name" className={labelCls}>{t("quote2.lName")}</Label>
          <Input id="qcf-name" data-testid="qc-name-input" required value={form.name} onChange={set("name")} placeholder={t("quote2.phName")} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qcf-company" className={labelCls}>{t("quote2.lCompany")}</Label>
          <Input id="qcf-company" data-testid="qc-company-input" value={form.company} onChange={set("company")} placeholder={t("quote2.phCompany")} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qcf-email" className={labelCls}>{t("quote2.lEmail")}</Label>
          <Input id="qcf-email" data-testid="qc-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t("quote2.phEmail")} className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qcf-country" className={labelCls}>{t("quote2.lCountry")}</Label>
          <CountrySelect
            id="qcf-country"
            testid="qc-country-select"
            required
            value={form.country}
            onChange={(v) => setForm((f) => ({ ...f, country: v }))}
            options={COUNTRIES}
            placeholder={t("quote2.phCountry")}
            className="w-full h-9 border border-border bg-white px-3 text-sm text-gray-950 focus:outline-none focus:border-gray-950 placeholder:text-gray-400"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="qcf-phone" className={labelCls}>{t("quote2.lPhone")}</Label>
          <div className="flex">
            {dial && <span className="flex items-center border border-r-0 border-border bg-gray-100 px-2.5 font-mono-tech text-xs text-gray-700 shrink-0">{dial}</span>}
            <Input id="qcf-phone" data-testid="qc-phone-input" value={form.phone} onChange={set("phone")} placeholder={dial ? "555 000 0000" : t("quote2.phPhone")} className={inputCls} />
          </div>
        </div>
        {office && (
          <div className="border-l-2 border-yellow-500 border-t border-r border-b border-border bg-gray-50 p-4">
            <p className={`${labelCls} mb-1.5`}>{t("rq.handledBy")}</p>
            <p className="text-xs font-semibold text-gray-950">{office.company}</p>
            <p className="text-xs text-gray-600">{office.address}</p>
          </div>
        )}
        <PrivacyConsent id="qc-privacy-consent" />
      </div>
      <div className="border-t border-border p-5 bg-gray-50">
        <Button data-testid="qc-submit-button" type="submit" disabled={sending} className="w-full bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black rounded-none font-mono-tech text-xs uppercase tracking-[0.25em] py-6 transition-colors">
          {sending ? t("quote2.sending") : t("quote2.submit")}
        </Button>
      </div>
    </form>
  );
};

export const QuoteCartDrawer = () => {
  const { t } = useTranslation();
  const { open, stage, close } = useQuoteCart();
  const [doneCount, setDoneCount] = useState(0);

  return (
    <>
      <div
        data-testid="quote-cart-overlay"
        onClick={close}
        className={`fixed inset-0 z-[108] bg-black/50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="quote-cart-drawer"
        className={`fixed inset-y-0 right-0 z-[110] w-full sm:w-[460px] bg-white flex flex-col shadow-[-20px_0_60px_rgba(0,0,0,0.25)] transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-[#0A0A0A]">
          <div>
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-yellow-500">{t("quote2.kicker")}</p>
            <h2 className="font-display font-black uppercase text-lg tracking-tight text-white">
              {stage === "add" ? t("qc.addTitle") : t("qc.drawerTitle")}
            </h2>
          </div>
          <button type="button" data-testid="quote-cart-close" onClick={close} className="text-gray-400 hover:text-white transition-colors p-1">
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col">
          {stage === "add" && <div className="overflow-y-auto"><AddStage /></div>}
          {stage === "cart" && <CartStage />}
          {stage === "form" && <FormStage onDone={setDoneCount} />}
          {stage === "done" && (
            <div className="p-8 text-center space-y-4" data-testid="qc-done">
              <div className="mx-auto w-12 h-12 bg-yellow-500 flex items-center justify-center">
                <Check size={24} className="text-black" />
              </div>
              <h3 className="font-display font-black uppercase text-xl tracking-tight text-gray-950">{t("quote2.doneTitle")}</h3>
              <p className="text-sm text-gray-600">{t("qc.doneBody", { count: doneCount })}</p>
              <Button onClick={close} variant="outline" className="rounded-none font-mono-tech text-xs uppercase tracking-[0.2em]">
                {t("qc.cancel")}
              </Button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
